import { useState, useEffect } from "react";
import { C, S, BASE } from '../constants';
import { fmtUAH } from '../utils';
import Img from '../components/Img';
import ProdLink from '../components/ProdLink';
import { FfBadge, Badge } from '../components/badges';

const STATUS_DOT = {
  successful: C.green,
  cancelled:  C.pink,
  pending:    C.yellow,
};

const STATUS_LABEL = {
  successful: "Успішне",
  cancelled:  "Скасовано",
  pending:    "В обробці",
};

const NP_RECEIVED = new Set([9, 10, 11]);
const NP_RETURNED = new Set([102, 103, 105, 106, 111]);
function npColor(code) {
  if (NP_RECEIVED.has(code)) return "#22c55e";
  if (NP_RETURNED.has(code)) return "#ef4444";
  return "#eab308";
}
const NP_PAYER = { Sender: "відправник", Recipient: "отримувач" };
function fmtDate(iso) {
  if (!iso) return "";
  const [d] = iso.split("T");
  if (!d) return iso;
  const [y, m, day] = d.split("-");
  return `${day}.${m}.${y}`;
}

const FILTERS = [
  { l: "Всі",          v: null,  key: "all" },
  { l: "В обробці",   v: 2,     key: "pending" },
  { l: "Успішні",      v: 1,     key: "successful" },
  { l: "Відмінені",   v: 3,     key: "cancelled" },
];

function fmtOrderDate(iso) {
  if (!iso) return "";
  // Rozetka stores times in Kyiv TZ — display as-is without UTC conversion
  const [date, time] = iso.split("T");
  if (!date || !time) return iso;
  const [y, m, d] = date.split("-");
  const months = ["","січ","лют","бер","квіт","трав","черв","лип","серп","вер","жовт","лист","груд"];
  return `${parseInt(d)} ${months[parseInt(m)]} ${time.slice(0, 5)}`;
}

function shortName(name) {
  if (!name) return "";
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0];
  return parts[0] + " " + parts[1][0] + ".";
}

function shortPayment(method) {
  if (!method) return "";
  if (/картк|card/i.test(method)) return "Карткою";
  if (/накладн|cod|наложен/i.test(method)) return "Накладний";
  if (/готівк|cash/i.test(method)) return "Готівкою";
  if (/онлайн|online/i.test(method)) return "Онлайн";
  return method.slice(0, 18);
}

export default function OrdersPage({
  orders, ordersLoading,
  ordersPage, ordersFilter, ordersSearch,
  setOrdersFilter, setOrdersSearch,
  loadOrders, dateFrom, dateTo,
}) {
  const [expanded, setExpanded] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [npOnly, setNpOnly] = useState(false);
  const [deliveryFilter, setDeliveryFilter] = useState("");

  useEffect(() => {
    fetch(BASE + "/api/warehouse/warehouses", {
      headers: { Authorization: "Bearer " + localStorage.getItem("crm_token") },
    })
      .then(r => r.ok ? r.json() : { warehouses: [] })
      .then(d => setWarehouses((d.warehouses || []).filter(w => w.kind === "own")))
      .catch(() => {});
  }, []);

  const setOrderWarehouse = async (dbId, whId) => {
    await fetch(BASE + `/api/orders/${dbId}/source-warehouse`, {
      method: "PATCH",
      headers: { Authorization: "Bearer " + localStorage.getItem("crm_token"), "Content-Type": "application/json" },
      body: JSON.stringify({ warehouse_id: whId }),
    });
    loadOrders(ordersPage, ordersFilter, dateFrom, dateTo, ordersSearch || null);
  };

  const total = orders?.total || 0;
  const counts = orders?.counts || {};

  return (
    <div>
      <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 14 }}>Замовлення</div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        {FILTERS.map(f => {
          const cnt = counts[f.key];
          const active = ordersFilter === f.v;
          return (
            <div key={String(f.v)} onClick={() => {
              setOrdersFilter(f.v); setNpOnly(false);
              loadOrders(1, f.v, dateFrom, dateTo, ordersSearch || null, false, deliveryFilter || null);
            }} style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "6px 12px", borderRadius: 8, fontSize: 13, cursor: "pointer",
              fontWeight: active ? 700 : 400,
              background: active ? C.blue : C.bg2,
              color: active ? "#fff" : C.text2,
              border: `1px solid ${active ? C.blue : C.border}`,
              transition: "all .15s",
            }}>
              {f.l}
              {cnt != null && (
                <span style={{
                  background: active ? "rgba(255,255,255,.25)" : (f.key === "pending" ? C.yellow : f.key === "cancelled" ? C.pink : C.bg1),
                  color: active ? "#fff" : (f.key === "pending" ? "#000" : C.text2),
                  borderRadius: 20, padding: "1px 7px", fontSize: 11, fontWeight: 700,
                }}>{cnt}</span>
              )}
            </div>
          );
        })}
        <div onClick={() => {
          const next = !npOnly; setNpOnly(next);
          loadOrders(1, next ? null : ordersFilter, dateFrom, dateTo, ordersSearch || null, next, deliveryFilter || null);
        }} title="Повернення/відмови, прибуло але не забрали, застрягло — всі служби доставки"
          style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "6px 12px", borderRadius: 8, fontSize: 13, cursor: "pointer",
            fontWeight: npOnly ? 700 : 400,
            background: npOnly ? C.pink : C.bg2,
            color: npOnly ? "#fff" : C.text2,
            border: `1px solid ${npOnly ? C.pink : C.border}`,
          }}>
          🚚 Проблемні доставки
          {counts.np_problem != null && (
            <span style={{
              background: npOnly ? "rgba(255,255,255,.25)" : C.pink,
              color: npOnly ? "#fff" : "#000",
              borderRadius: 20, padding: "1px 7px", fontSize: 11, fontWeight: 700,
            }}>{counts.np_problem}</span>
          )}
        </div>
        <select
          value={deliveryFilter}
          onChange={e => {
            const v = e.target.value; setDeliveryFilter(v);
            loadOrders(1, npOnly ? null : ordersFilter, dateFrom, dateTo, ordersSearch || null, npOnly, v || null);
          }}
          style={{ ...S.input, width: 200, fontSize: 13 }}
          title="Фільтр за службою доставки"
        >
          <option value="">Всі служби доставки</option>
          {(orders?.delivery_methods || []).map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <input
          placeholder="Пошук по номеру, товару, артикулу..."
          value={ordersSearch}
          onChange={e => setOrdersSearch(e.target.value)}
          style={{ ...S.input, width: 240, marginLeft: 8 }}
        />
        <span style={{ marginLeft: "auto", color: C.text3, fontSize: 12 }}>Всього: {total}</span>
      </div>

      {/* Summary strip for current filter */}
      {orders?.summary && (
        <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
          {[
            ["Замовлень", orders.summary.orders],
            ["Виручка", `${fmtUAH(orders.summary.revenue)}`],
            ["Утримано Rozetka (факт)", `${fmtUAH(orders.summary.rozetka_fee_actual)}`],
            ["Повернень", orders.summary.returns],
          ].map(([l, v]) => (
            <div key={l} style={{ ...S.card, padding: "8px 14px", flex: "0 0 auto" }}>
              <div style={{ fontSize: 10, color: C.text3, textTransform: "uppercase", letterSpacing: ".5px" }}>{l}</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: C.text1 }}>{v}</div>
            </div>
          ))}
        </div>
      )}

      {ordersLoading
        ? <div style={{ textAlign: "center", color: C.text3, padding: 60 }}>Завантаження...</div>
        : (
          <>
            {(orders?.orders || []).map(o => {
              const isExp = expanded === o.id;
              const dotColor = STATUS_DOT[o.status_label] || C.text3;
              const buyer = shortName(o.buyer_name);
              const payment = shortPayment(o.payment_method);
              const thumbs = (o.items || []).filter(i => i.image_url).slice(0, 3);
              const extraThumbs = (o.items_count || o.items?.length || 0) - thumbs.length;

              return (
                <div key={o.id} style={{ ...S.card, marginBottom: 6, padding: 0, overflow: "hidden" }}>
                  {/* Collapsed row */}
                  <div
                    onClick={() => setExpanded(isExp ? null : o.id)}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", cursor: "pointer" }}
                  >
                    {/* Status dot */}
                    <div style={{
                      width: 8, height: 8, borderRadius: "50%",
                      background: dotColor, flexShrink: 0,
                      boxShadow: `0 0 5px ${dotColor}`,
                    }} />

                    {/* Order # */}
                    <span style={{ fontSize: 12, fontFamily: "monospace", color: C.blue, flexShrink: 0, width: 88 }}>
                      #{o.id}
                    </span>

                    {/* Date */}
                    <span style={{ fontSize: 11, color: C.text3, flexShrink: 0, width: 110 }}>{fmtOrderDate(o.created_at)}</span>

                    {/* Thumbnails — fixed block, bigger */}
                    <div style={{ display: "flex", gap: 4, alignItems: "center", flexShrink: 0 }}>
                      {thumbs.map((item, idx) => (
                        <Img key={idx} src={item.image_url} size={38} />
                      ))}
                      {extraThumbs > 0 && (
                        <span style={{
                          fontSize: 11, color: C.text3, whiteSpace: "nowrap",
                          background: C.bg1, borderRadius: 6, padding: "2px 6px",
                        }}>+{extraThumbs}</span>
                      )}
                    </div>

                    {/* Buyer + payment — takes remaining space */}
                    <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                      {buyer && (
                        <span style={{ fontSize: 12, color: C.text1, fontWeight: 500,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{buyer}</span>
                      )}
                      {payment && (
                        <span style={{ fontSize: 11, color: C.text3 }}>💳 {payment}</span>
                      )}
                    </div>

                    {/* Badges */}
                    {o.marketplace === 'epicentr' && <Badge color="#f97316">EPC</Badge>}
                    <FfBadge isFf={o.is_fulfillment} />
                    {o.from_warehouse && <Badge color={C.yellow}>Викуп</Badge>}
                    {o.np_problem_reason && (
                      <span style={{
                        fontSize: 10, fontWeight: 700, flexShrink: 0, whiteSpace: "nowrap",
                        padding: "2px 7px", borderRadius: 20,
                        background: `${C.pink}22`, color: C.pink, border: `1px solid ${C.pink}55`,
                      }}>🚚 {o.np_problem_reason}</span>
                    )}

                    {/* Status text */}
                    <span style={{
                      fontSize: 11, color: dotColor, flexShrink: 0,
                      width: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      textAlign: "right",
                    }}>
                      {o.status_name ? o.status_name.slice(0, 24) : STATUS_LABEL[o.status_label]}
                    </span>

                    {/* Amount */}
                    <span style={{ fontSize: 14, fontWeight: 800, color: C.text1, flexShrink: 0, whiteSpace: "nowrap", minWidth: 70, textAlign: "right" }}>
                      {fmtUAH(o.amount)}
                    </span>

                    {/* Link to seller center */}
                    {o.marketplace !== 'epicentr' && (
                      <a
                        href={`https://seller.rozetka.com.ua/orders/${o.id}`}
                        target="_blank" rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{
                          fontSize: 11, color: C.blue, flexShrink: 0,
                          padding: "3px 8px", borderRadius: 5,
                          border: `1px solid ${C.border}`, background: C.bg2,
                          textDecoration: "none", lineHeight: 1.6,
                        }}
                      >↗</a>
                    )}

                    <span style={{ color: C.text4, fontSize: 12, flexShrink: 0 }}>{isExp ? "▲" : "▼"}</span>
                  </div>

                  {/* Expanded */}
                  {isExp && (
                    <div style={{ borderTop: `1px solid ${C.border}`, padding: "12px 14px", overflowX: "auto" }}>
                      {/* Order meta */}
                      <div style={{
                        display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px 20px",
                        fontSize: 12, marginBottom: 14, background: "rgba(255,255,255,.03)",
                        borderRadius: 8, padding: "10px 14px", border: `1px solid ${C.border}`,
                      }}>
                        {o.buyer_name && (
                          <div>
                            <div style={{ color: C.text3, fontSize: 11, marginBottom: 2 }}>Отримувач</div>
                            <div style={{ color: C.text1, fontWeight: 600 }}>{o.buyer_name}</div>
                          </div>
                        )}
                        {o.buyer_phone && (
                          <div>
                            <div style={{ color: C.text3, fontSize: 11, marginBottom: 2 }}>Телефон</div>
                            <div style={{ color: C.blue, fontFamily: "monospace" }}>
                              +{o.buyer_phone.replace(/^\+/, "")}
                            </div>
                          </div>
                        )}
                        {o.payment_method && (
                          <div>
                            <div style={{ color: C.text3, fontSize: 11, marginBottom: 2 }}>Оплата</div>
                            <div style={{ color: C.text2 }}>{o.payment_method}</div>
                          </div>
                        )}
                        {o.delivery_service && (
                          <div>
                            <div style={{ color: C.text3, fontSize: 11, marginBottom: 2 }}>Доставка</div>
                            <div style={{ color: C.text2 }}>{o.delivery_service}</div>
                          </div>
                        )}
                        {o.delivery_address && (
                          <div style={{ gridColumn: "span 2" }}>
                            <div style={{ color: C.text3, fontSize: 11, marginBottom: 2 }}>Адреса</div>
                            <div style={{ color: C.text2 }}>{o.delivery_address}</div>
                          </div>
                        )}
                        {o.ttn && (
                          <div style={{ gridColumn: "1 / -1" }}>
                            <div style={{ color: C.text3, fontSize: 11, marginBottom: 2 }}>ТТН</div>
                            <a href={`https://novaposhta.ua/tracking/?cargo_number=${o.ttn}`} target="_blank" rel="noreferrer"
                               onClick={e => e.stopPropagation()}
                               style={{ color: C.yellow, fontFamily: "monospace", fontSize: 13, fontWeight: 700, letterSpacing: 1, textDecoration: "none" }}>
                              {o.ttn} ↗
                            </a>
                          </div>
                        )}
                        <div>
                          <div style={{ color: C.text3, fontSize: 11, marginBottom: 2 }}>Статус</div>
                          <div style={{ color: dotColor }}>{o.status_name || STATUS_LABEL[o.status_label]}</div>
                        </div>
                        {o.refund_status && (
                          <div>
                            <div style={{ color: C.text3, fontSize: 11, marginBottom: 2 }}>Повернення</div>
                            <div style={{ color: C.pink }}>{o.refund_status}</div>
                          </div>
                        )}
                        {o.bonus_amount > 0 && (
                          <div>
                            <div style={{ color: C.text3, fontSize: 11, marginBottom: 2 }}>Бонуси</div>
                            <div style={{ color: C.text2 }}>{fmtUAH(o.bonus_amount)}</div>
                          </div>
                        )}
                        {o.is_smart && (
                          <div>
                            <div style={{ color: C.text3, fontSize: 11, marginBottom: 2 }}>Програма</div>
                            <Badge color={C.purple || C.blue}>Smart</Badge>
                          </div>
                        )}
                        {!o.is_fulfillment && warehouses.length > 0 && (
                          <div>
                            <div style={{ color: C.text3, fontSize: 11, marginBottom: 2 }}>Склад відвантаження (FBS)</div>
                            <select
                              value={o.source_warehouse_id || (warehouses.find(w => w.is_default_fbs)?.id ?? "")}
                              onChange={e => setOrderWarehouse(o.db_id, parseInt(e.target.value, 10))}
                              onClick={e => e.stopPropagation()}
                              style={{ ...S.input, padding: "4px 8px", fontSize: 12 }}
                            >
                              {warehouses.map(w => (
                                <option key={w.id} value={w.id}>{w.name}{w.is_default_fbs ? " ★" : ""}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>

                      {/* Economics mini-P&L (order level) */}
                      {(() => {
                        const rev = o.revenue ?? 0;
                        const fee = o.rozetka_fee ?? 0;
                        const profit = o.order_profit ?? 0;
                        const cost = Math.round((rev - fee - profit) * 100) / 100;
                        const Cell = ({ label, val, op, color }) => (
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            {op && <span style={{ color: C.text4, fontSize: 16 }}>{op}</span>}
                            <div>
                              <div style={{ fontSize: 10, color: C.text3, textTransform: "uppercase", letterSpacing: ".5px" }}>{label}</div>
                              <div style={{ fontSize: 15, fontWeight: 700, color: color || C.text1 }}>{fmtUAH(val)}</div>
                            </div>
                          </div>
                        );
                        return (
                          <div style={{
                            display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
                            marginBottom: 14, background: "rgba(255,255,255,.03)", borderRadius: 8,
                            padding: "10px 14px", border: `1px solid ${C.border}`,
                          }}>
                            <Cell label="Виручка" val={rev} />
                            <Cell label={`Утримано Rozetka${o.fee_is_real ? " (факт)" : " (оцінка)"}`} val={fee} op="−" color={C.yellow} />
                            <Cell label="Собівартість" val={cost} op="−" />
                            <Cell label="Прибуток" val={profit} op="=" color={profit >= 0 ? C.green : C.pink} />
                          </div>
                        );
                      })()}

                      {/* Nova Poshta delivery block */}
                      {o.np_status && (
                        <div style={{
                          marginBottom: 14, background: "rgba(255,255,255,.03)", borderRadius: 8,
                          padding: "10px 14px", border: `1px solid ${C.border}`,
                          display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px 20px", fontSize: 12,
                        }}>
                          <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 11, color: C.text3 }}>Доставка Nova Poshta:</span>
                            <span style={{ color: npColor(o.np_status_code), fontWeight: 700 }}>{o.np_status}</span>
                            {o.np_problem_reason && (
                              <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 20,
                                background: `${C.pink}22`, color: C.pink, border: `1px solid ${C.pink}55` }}>⚠ {o.np_problem_reason}</span>
                            )}
                          </div>
                          {o.np_delivered_at ? (
                            <div><div style={{ color: C.text3, fontSize: 11, marginBottom: 2 }}>Отримано</div>
                              <div style={{ color: C.green }}>{fmtDate(o.np_delivered_at)}</div></div>
                          ) : o.np_scheduled_at ? (
                            <div><div style={{ color: C.text3, fontSize: 11, marginBottom: 2 }}>Прибуття (план)</div>
                              <div style={{ color: C.text2 }}>{fmtDate(o.np_scheduled_at)}</div></div>
                          ) : null}
                          {o.np_doc_cost != null && o.np_doc_cost > 0 && (
                            <div><div style={{ color: C.text3, fontSize: 11, marginBottom: 2 }}>Вартість доставки</div>
                              <div style={{ color: C.text1 }}>{o.np_doc_cost} ₴ <span style={{ color: C.text3 }}>({NP_PAYER[o.np_payer] || o.np_payer || "—"})</span></div></div>
                          )}
                          {o.np_cod_sum != null && o.np_cod_sum > 0 && (
                            <div><div style={{ color: C.text3, fontSize: 11, marginBottom: 2 }}>Накладений платіж</div>
                              <div style={{ color: C.yellow }}>{o.np_cod_sum} ₴</div></div>
                          )}
                          {o.np_recipient_warehouse && (
                            <div style={{ gridColumn: "1 / -1" }}><div style={{ color: C.text3, fontSize: 11, marginBottom: 2 }}>Відділення</div>
                              <div style={{ color: C.text2 }}>{o.np_recipient_warehouse}</div></div>
                          )}
                        </div>
                      )}

                      {/* Items table */}
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 700 }}>
                        <thead>
                          <tr>
                            {["Фото","ID товару","Товар","Арт.","К-сть","Ціна","Факт","Собів."].map(h => (
                              <th key={h} style={S.th(false)}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {o.items.map((item, idx) => (
                            <tr key={idx} style={{ background: item.is_loss ? "rgba(244,114,182,.05)" : "transparent" }}>
                              <td style={S.td}><Img src={item.image_url} size={30} /></td>
                              <td style={{ ...S.td, color: C.blue, fontFamily: "monospace", fontSize: 11 }}>
                                {item.rozetka_item_id}
                              </td>
                              <td style={{ ...S.td, maxWidth: 200 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
                                  {item.is_loss && <span style={{ color: C.pink, flexShrink: 0 }}>⚠</span>}
                                  <ProdLink name={item.name} productId={item.rozetka_item_id} marketplace={o.marketplace} style={{ fontSize: 13, fontWeight: 500, maxWidth: 180 }} />
                                </div>
                              </td>
                              <td style={{ ...S.td, color: C.text4, fontFamily: "monospace", fontSize: 11 }}>{item.article}</td>
                              <td style={{ ...S.td, color: C.text2 }}>{item.qty}</td>
                              <td style={{ ...S.td, color: item.is_promo ? C.text4 : C.text1, textDecoration: item.is_promo ? "line-through" : "none" }}>{fmtUAH(item.price)}</td>
                              <td style={{ ...S.td, color: C.text1, fontWeight: 600 }}>
                                {fmtUAH(item.is_promo && item.promo_price ? item.promo_price : item.price)}
                              </td>
                              <td style={{ ...S.td, color: C.text2 }}>{item.cost_price > 0 ? fmtUAH(item.cost_price) : "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Pagination */}
            {total > 50 && (
              <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 18, alignItems: "center" }}>
                <button
                  onClick={() => loadOrders(ordersPage - 1, ordersFilter, dateFrom, dateTo, ordersSearch || null)}
                  disabled={ordersPage <= 1}
                  style={{ padding: "7px 18px", borderRadius: 8, background: C.bg2,
                    border: `1px solid ${C.border}`, color: ordersPage <= 1 ? C.border : C.blue,
                    cursor: ordersPage <= 1 ? "default" : "pointer", fontSize: 13 }}
                >← Назад</button>
                <span style={{ color: C.text3, fontSize: 13 }}>
                  {ordersPage} / {Math.ceil(total / 50)}
                </span>
                <button
                  onClick={() => loadOrders(ordersPage + 1, ordersFilter, dateFrom, dateTo, ordersSearch || null)}
                  disabled={ordersPage >= Math.ceil(total / 50)}
                  style={{ padding: "7px 18px", borderRadius: 8, background: C.bg2,
                    border: `1px solid ${C.border}`,
                    color: ordersPage >= Math.ceil(total / 50) ? C.border : C.blue,
                    cursor: ordersPage >= Math.ceil(total / 50) ? "default" : "pointer", fontSize: 13 }}
                >Вперед →</button>
              </div>
            )}
          </>
        )
      }
    </div>
  );
}
